import { useEffect, useState } from "react"
import { useQuery } from "urql"

import type { Query, User } from "@/graphql/codegen/graphql"
import { UsersListDocument } from "@/graphql/codegen/graphql"

const LIMIT = 100

interface UseGetUsersProps {
  userIds?: string[]
  query?: string
  pause?: boolean
}

const useGetUsers = ({
  query = "",
  userIds,
  pause = false,
}: UseGetUsersProps): {
  userList: User[]
  fetching: boolean
  loadMore: () => void
  hasMore: boolean
} => {
  const [offset, setOffset] = useState(0)
  const [userList, setUserList] = useState<User[]>([])
  const [hasMore, setHasMore] = useState<boolean>(false)

  const [{ data, fetching }] = useQuery<Pick<Query, "user">>({
    query: UsersListDocument,
    variables: { input: { filter: { query, ...(userIds?.length && { userIds }) }, limit: LIMIT, offset } },
    pause,
  })

  useEffect(() => {
    if (!data) return

    const users = data?.user?.list?.__typename === "UserListSuccess" ? data?.user?.list?.users : []

    setUserList((prev) => {
      const uniqueIds = new Set(prev.map((user) => user.userId))
      return offset === 0 ? users : [...prev, ...users.filter((user) => !uniqueIds.has(user.userId))]
    })
    setHasMore(users.length % LIMIT === 0)
  }, [data, offset])

  const loadMore = () => {
    setOffset((prevOffset) => prevOffset + LIMIT)
  }

  useEffect(() => {
    setOffset(0)
  }, [query])

  return {
    userList,
    fetching,
    loadMore,
    hasMore,
  }
}

export default useGetUsers
